/* -------------------------------------------------------------------------- */
/*                                   Datatable                                  */
/* -------------------------------------------------------------------------- */

import { ENUM_UNITS } from '@config/constants/units';
import i18n from '@config/i18n/config';
import {
	TProduction,
	TProductionPopulatedParsed,
} from '@features/sourcing/productions/types';
import sumBy from 'lodash/sumBy';

export function addFooterColumn(
	productions: TProductionPopulatedParsed[],
	columns: any
) {
	const _newColumns = columns.map((item: any) => {
		const footerType = item?.footerType;
		switch (footerType) {
			case 'sumProductions':
				const uniqueUnits = [
					...new Set(productions.map((p) => p.unit)),
				];
				const isMultiUnit = uniqueUnits.length > 1;

				if (isMultiUnit) {
					item.footer = `${uniqueUnits.length} ${i18n.t('global.unit')}(s)`;
				} else {
					let total = sumBy(
						productions,
						'productionNumeric'
					);
					const rawUnit =
						uniqueUnits[0] as keyof typeof ENUM_UNITS;
					let unitLabel: string = ENUM_UNITS.MWH;

					if (rawUnit === 'KWH') {
						unitLabel = ENUM_UNITS.MWH;
					} else if (rawUnit === 'KG') {
						unitLabel = ENUM_UNITS.t;
					} else if (rawUnit === 'ITEM') {
						unitLabel = ENUM_UNITS.ITEM;
					} else {
						unitLabel =
							ENUM_UNITS[rawUnit] || rawUnit || 'item';
					}

					item.footer = `${Number(total.toFixed(3))} ${unitLabel}`;
				}

				return item;

			case 'sumTokenEquivalents':
				item.footer = 'sumTokenEquivalents';
				const sumTokenEquivalents = sumBy(
					productions,
					function (o) {
						return o.tokenEquivalentNumeric;
					}
				);
				item.footer = `${sumTokenEquivalents.toFixed(3)} ${ENUM_UNITS.tco2e}`;
				return item;

			case 'sumProjectsProductions':
				const uniqueProductionsByProject = [
					...new Set(
						productions.map((item) => item.projectId)
					),
				];

				item.footer = 'sumProjectsProductions';

				item.footer = `∑ ${uniqueProductionsByProject.length} ${i18n.t('global.projects')}`;
				return item;

			case 'sumSitesProductions':
				const uniqueProductionsBySite = [
					...new Set(
						productions.map((item) => item.siteId)
					),
				];

				item.footer = 'sumProjectsProductions';

				item.footer = `∑ ${uniqueProductionsBySite.length} ${i18n.t('global.sites')}`;
				return item;

			case 'sumOrganizationsProductions':
				const uniqueProductionsByOrganization = [
					...new Set(
						productions.map((item) => item.siteId)
					),
				];

				item.footer = 'sumOrganizationsProductions';

				item.footer = `∑ ${uniqueProductionsByOrganization.length} ${i18n.t('global.organizations')}`;
				return item;

			case 'sumVerifiedProductions':
				const uniqueProductionsByVerified =
					productions.filter((item) => item.verified);

				item.footer = 'sumVerifiedProductions';

				item.footer = `∑ ${uniqueProductionsByVerified.length} ${i18n.t('global.verified')}`;
				return item;

			case 'sumMintStatusProductions':
				const uniqueProductionsByMintStatus =
					productions.filter(
						(item) => item.status === 'MINTED'
					);

				item.footer = 'sumMintStatusProductions';

				item.footer = `∑ ${uniqueProductionsByMintStatus.length} ${i18n.t('global.Minted')}`;
				return item;

			default:
				return item;
		}
	});

	return _newColumns;
}

export function addFooterProductionsForMintColumn(
	productions: TProduction[] | undefined,
	columns: any
) {
	const safeProductions = Array.isArray(productions)
		? productions
		: [];
	const hasData = safeProductions.length > 0;

	return columns.map((item: any) => {
		const newItem = { ...item };
		const footerType = newItem?.footerType;

		if (!footerType) return newItem;

		switch (footerType) {
			case 'sumProductions':
				if (!hasData) {
					newItem.footer = `0 ${i18n.t('global.unit')}`;
				} else {
					const uniqueUnits = [
						...new Set(
							safeProductions.map((p) => p.unit)
						),
					];
					const isMultiUnit = uniqueUnits.length > 1;

					if (isMultiUnit) {
						newItem.footer = `${uniqueUnits.length} ${i18n.t('global.unit')}(s)`;
					} else {
						let total = sumBy(
							safeProductions,
							'quantityNumeric'
						);
						const rawUnit =
							uniqueUnits[0] as keyof typeof ENUM_UNITS;
						let unitLabel =
							ENUM_UNITS[rawUnit] || rawUnit || 'item';

						if (rawUnit === 'KWH')
							unitLabel = ENUM_UNITS.MWH;
						else if (rawUnit === 'KG')
							unitLabel = ENUM_UNITS.t;

						newItem.footer = `${Number(total.toFixed(3))} ${unitLabel}`;
					}
				}
				break;

			case 'sumTokenEquivalents':
				if (!hasData) {
					newItem.footer = `0.000 ${ENUM_UNITS.tco2e}`;
				} else {
					const sumTokens =
						sumBy(
							safeProductions,
							'tokenEquivalentNumeric'
						) || 0;
					newItem.footer = `${sumTokens.toFixed(3)} ${ENUM_UNITS.tco2e}`;
				}
				break;

			case 'sumSitesProductions':
				if (!hasData) {
					newItem.footer = `∑ 0 ${i18n.t('global.sites')}`;
				} else {
					const uniqueSites = [
						...new Set(
							safeProductions.map((p) => p.siteId)
						),
					];
					newItem.footer = `∑ ${uniqueSites.length} ${i18n.t('global.sites')}`;
				}
				break;

			default:
				break;
		}

		return newItem;
	});
}
